// src/vatandas/components/location/PreciseLocationPicker.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import vatandasApi from "../../services/vatandasApi";
import { MapContainer, TileLayer, Marker, Polygon, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import "./PreciseLocationPicker.css";

import {
  MapPinIcon,
  ArrowPathIcon,
  XMarkIcon,
  SignalIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";

// Leaflet default icon fix (component-scope)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

/* =========================
   Utils
========================= */

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function round6(v) {
  return Number.parseFloat(Number(v).toFixed(6));
}

function isFiniteNumber(v) {
  return typeof v === "number" && Number.isFinite(v);
}

function nowMs() {
  return Date.now();
}

/**
 * Create a "circle-like" polygon around a point using destination-point formula.
 * Used to show accuracy / uncertainty zone.
 */
function circleToPolygon(lat, lng, radiusMeters, points = 48) {
  const R = 6378137;
  const latRad = (lat * Math.PI) / 180;
  const lngRad = (lng * Math.PI) / 180;
  const angDist = radiusMeters / R;

  const out = [];
  for (let i = 0; i < points; i++) {
    const brng = (i * 360) / points;
    const brngRad = (brng * Math.PI) / 180;

    const sinLat1 = Math.sin(latRad);
    const cosLat1 = Math.cos(latRad);

    const sinAng = Math.sin(angDist);
    const cosAng = Math.cos(angDist);

    const sinLat2 = sinLat1 * cosAng + cosLat1 * sinAng * Math.cos(brngRad);
    const lat2 = Math.asin(sinLat2);

    const y = Math.sin(brngRad) * sinAng * cosLat1;
    const x = cosAng - sinLat1 * sinLat2;
    const lng2 = lngRad + Math.atan2(y, x);

    const lngDeg = ((((lng2 * 180) / Math.PI) + 540) % 360) - 180;
    out.push([round6((lat2 * 180) / Math.PI), round6(lngDeg)]);
  }
  return out;
}

/* =========================
   Map click handler
========================= */
function MapClick({ onPick, disabled }) {
  useMapEvents({
    click: (e) => {
      if (disabled) return;
      const lat = round6(e.latlng.lat);
      const lng = round6(e.latlng.lng);
      onPick?.({ lat, lng });
    },
  });
  return null;
}

/* =========================
   Component
========================= */

export default function PreciseLocationPicker({
  value, // { lat, lng } | null
  onChange, // ({ lat, lng, mode, accuracy }) => void  OR null
  required = false,

  // If title="" we render compact mode (header/hint hidden)
  title = "Başvurunuzun Konumu",

  defaultCenter = [36.43792100436059, 35.90175786356508],
  defaultZoom = 12,
  minZoom = 11,

  autoZoom = 17,
}) {
  const compact = !String(title || "").trim();

  const mapRef = useRef(null);
  const pendingViewRef = useRef(null);

  const watchIdRef = useRef(null);
  const activeRunRef = useRef(0);
  const unmountedRef = useRef(false);

  const samplesRef = useRef([]);
  const firstFixAtRef = useRef(null);
  const autoCenteredRef = useRef(false);

  const [mode, setMode] = useState("none"); // none | gps | manual
  const [status, setStatus] = useState("idle"); // idle | searching | fixed | error
  const [accuracy, setAccuracy] = useState(null); // meters
  const [avgAccuracy, setAvgAccuracy] = useState(null); // meters (info)
  const [infoText, setInfoText] = useState(null); // small banner (compact) or inline alert (non-compact)

  // Harita başlangıç merkezi/zoom'u İdari Sınırlar'dan (PUBLIC endpoint, belediye-özel)
  // gelir → farklı belediyede (Arsuz/Kırıkhan) doğru şehir açılır. Yoksa prop fallback.
  // Public endpoint YALNIZ {center, zoom} döner (hassas veri yok).
  const [boundaryView, setBoundaryView] = useState(null);
  useEffect(() => {
    let iptal = false;
    vatandasApi
      .get("/bm4/boundaries/default-view/")
      .then((res) => {
        if (iptal) return;
        const lat = Number(res?.data?.center?.lat);
        const lng = Number(res?.data?.center?.lng);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          const z = Number(res?.data?.zoom) || null;
          setBoundaryView({ center: [lat, lng], zoom: z });
          // MapContainer center yalnız ilk mount → boundary sonradan gelince imperatif ortala
          const m = mapRef.current;
          if (m) m.setView([lat, lng], z ?? m.getZoom(), { animate: false });
        }
      })
      .catch(() => {});
    return () => {
      iptal = true;
    };
  }, []);

  const selected = useMemo(() => {
    if (!value) return null;
    if (!isFiniteNumber(value.lat) || !isFiniteNumber(value.lng)) return null;
    return [Number(value.lat), Number(value.lng)];
  }, [value]);

  const emit = useCallback(
    ({ lat, lng, nextMode, nextAcc }) => {
      const payload = {
        lat: round6(lat),
        lng: round6(lng),
        mode: nextMode || mode,
        accuracy: typeof nextAcc === "number" ? Math.round(nextAcc) : null,
      };
      onChange?.(payload);
    },
    [onChange, mode]
  );

  const invalidateSizeSoon = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;

    requestAnimationFrame(() => {
      try {
        map.invalidateSize();
      } catch {}
      window.setTimeout(() => {
        try {
          map.invalidateSize();
        } catch {}
      }, 120);
    });
  }, []);

  useEffect(() => {
    // Helps with grid/layout changes + mobile address bar resize etc.
    const onResize = () => invalidateSizeSoon();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [invalidateSizeSoon]);

  const safeFlyTo = useCallback(
    (lat, lng, zoom = autoZoom) => {
      const target = [lat, lng];
      const map = mapRef.current;

      const doIt = (m) => {
        if (!m) return;
        try {
          m.flyTo(target, zoom, { duration: 0.9 });
        } catch {
          try {
            m.setView(target, zoom, { animate: true });
          } catch {}
        }
        invalidateSizeSoon();
      };

      if (!map) {
        pendingViewRef.current = { target, zoom };
        return;
      }
      doIt(map);
    },
    [autoZoom, invalidateSizeSoon]
  );

  const handleMapCreated = useCallback(
    (map) => {
      mapRef.current = map;

      const pending = pendingViewRef.current;
      if (pending?.target) {
        pendingViewRef.current = null;
        try {
          map.flyTo(pending.target, pending.zoom ?? autoZoom, { duration: 0.9 });
        } catch {
          try {
            map.setView(pending.target, pending.zoom ?? autoZoom, { animate: true });
          } catch {}
        }
      }

      invalidateSizeSoon();
    },
    [autoZoom, invalidateSizeSoon]
  );

  useEffect(() => {
    return () => {
      unmountedRef.current = true;
      if (watchIdRef.current != null) {
        try {
          navigator.geolocation.clearWatch(watchIdRef.current);
        } catch {}
        watchIdRef.current = null;
      }
    };
  }, []);

  const stopWatch = useCallback(() => {
    if (watchIdRef.current != null) {
      try {
        navigator.geolocation.clearWatch(watchIdRef.current);
      } catch {}
      watchIdRef.current = null;
    }
  }, []);

  const resetRunState = useCallback(() => {
    samplesRef.current = [];
    firstFixAtRef.current = null;
    autoCenteredRef.current = false;

    setAccuracy(null);
    setAvgAccuracy(null);
    setInfoText(null);
  }, []);

  const clearLocation = useCallback(() => {
    stopWatch();
    activeRunRef.current += 1;

    resetRunState();
    setMode("none");
    setStatus("idle");

    onChange?.(null);
  }, [onChange, resetRunState, stopWatch]);

  const chooseStablePoint = useCallback(() => {
    const samples = samplesRef.current;
    if (!samples.length) return null;

    const HARD_ACC_MAX = 250;
    const filtered = samples.filter((s) => s.acc <= HARD_ACC_MAX);
    const arr = filtered.length ? filtered : samples;

    const best = arr.reduce((p, c) => (c.acc < p.acc ? c : p), arr[0]);

    let sumW = 0;
    let sumLat = 0;
    let sumLng = 0;

    for (const s of arr) {
      const denom = Math.pow(Math.max(6, s.acc), 2);
      const w = 1 / denom;
      sumW += w;
      sumLat += s.lat * w;
      sumLng += s.lng * w;
    }

    const lat = sumLat / Math.max(1e-9, sumW);
    const lng = sumLng / Math.max(1e-9, sumW);

    const avgAcc = Math.round(arr.reduce((a, s) => a + (Number.isFinite(s.acc) ? s.acc : 0), 0) / arr.length);

    return {
      lat: round6(lat),
      lng: round6(lng),
      bestAcc: Math.round(best.acc),
      avgAcc,
      samples: arr.length,
    };
  }, []);

  const locateMe = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setStatus("error");
      setInfoText("Tarayıcınız konum özelliğini desteklemiyor.");
      return;
    }

    const runId = activeRunRef.current + 1;
    activeRunRef.current = runId;

    stopWatch();
    resetRunState();

    setMode("gps");
    setStatus("searching");
    setInfoText(null);

    const startAt = nowMs();

    const MAX_TOTAL_MS = 12000;
    const REFINE_AFTER_FIRST_FIX_MS = 2500;
    const SOFT_TARGET = 20;

    const onSuccess = (pos) => {
      if (unmountedRef.current) return;
      if (activeRunRef.current !== runId) return;

      const lat = round6(pos.coords.latitude);
      const lng = round6(pos.coords.longitude);
      const acc = Math.round(pos.coords.accuracy || 999);

      samplesRef.current.push({ lat, lng, acc, t: nowMs() });
      setAccuracy(acc);

      const pick = chooseStablePoint();
      if (pick) {
        setAvgAccuracy(pick.avgAcc);
        emit({ lat: pick.lat, lng: pick.lng, nextMode: "gps", nextAcc: pick.bestAcc });

        if (!firstFixAtRef.current) {
          firstFixAtRef.current = nowMs();
          setStatus("fixed");
          setInfoText(null);

          if (!autoCenteredRef.current) {
            autoCenteredRef.current = true;
            safeFlyTo(pick.lat, pick.lng, autoZoom);
          }
        }
      }

      const elapsed = nowMs() - startAt;
      const firstFixElapsed = firstFixAtRef.current ? nowMs() - firstFixAtRef.current : null;

      const stable = pick?.bestAcc != null ? pick.bestAcc <= SOFT_TARGET : acc <= SOFT_TARGET;

      if (stable && samplesRef.current.length >= 2) {
        stopWatch();
        return;
      }
      if (firstFixElapsed != null && firstFixElapsed >= REFINE_AFTER_FIRST_FIX_MS) {
        stopWatch();
        return;
      }
      if (elapsed >= MAX_TOTAL_MS) {
        stopWatch();
      }
    };

    const onError = (err) => {
      if (unmountedRef.current) return;
      if (activeRunRef.current !== runId) return;

      stopWatch();

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          onSuccess(pos);
          stopWatch();
        },
        (err2) => {
          if (unmountedRef.current) return;
          if (activeRunRef.current !== runId) return;

          setStatus("error");
          setMode("none");
          setInfoText("Konum alınamadı. Haritada nokta seçebilirsiniz.");
          console.warn("Geolocation error:", err2?.message || err?.message || err2 || err);
        },
        { enableHighAccuracy: false, timeout: 9000, maximumAge: 1500 }
      );
    };

    watchIdRef.current = navigator.geolocation.watchPosition(onSuccess, onError, {
      enableHighAccuracy: true,
      timeout: 9000,
      maximumAge: 0,
    });
  }, [autoZoom, chooseStablePoint, emit, resetRunState, safeFlyTo, stopWatch]);

  const handlePick = useCallback(
    ({ lat, lng }) => {
      stopWatch();
      activeRunRef.current += 1;

      setMode("manual");
      setStatus("fixed");
      setInfoText(null);

      setAccuracy(null);
      setAvgAccuracy(null);

      emit({ lat, lng, nextMode: "manual", nextAcc: null });
      safeFlyTo(lat, lng, autoZoom);
    },
    [autoZoom, emit, safeFlyTo, stopWatch]
  );

  const zone = useMemo(() => {
    if (!selected) return null;
    if (typeof accuracy !== "number" || !Number.isFinite(accuracy)) return null;
    const radius = clamp(accuracy, 8, 260);
    return circleToPolygon(selected[0], selected[1], radius, 56);
  }, [selected, accuracy]);

  const isSearching = status === "searching";

  // Button content (compact = icon-only)
  const gpsBtn = useMemo(() => {
    if (isSearching) return { Icon: ArrowPathIcon, text: "GPS", spin: true, state: "searching" };
    if (status === "fixed" && mode === "gps") return { Icon: CheckCircleIcon, text: "GPS", spin: false, state: "fixed" };
    return { Icon: SignalIcon, text: "GPS", spin: false, state: "idle" };
  }, [isSearching, status, mode]);

  return (
    <div className={`vloc ${compact ? "vloc--compact" : ""} ${required ? "vloc--required" : ""}`}>
      {/* Non-compact header (legacy usage) */}
      {!compact ? (
        <>
          <div className="vloc-head">
            <div className="vloc-title">
              <MapPinIcon className="vloc-title-ic" aria-hidden="true" />
              <span>{title}</span>
            </div>

            <div className="vloc-actions">
              <button type="button" className="vloc-btn" onClick={locateMe} disabled={isSearching}>
                <gpsBtn.Icon className={`vloc-btn-ic ${gpsBtn.spin ? "spin" : ""}`} aria-hidden="true" />
                {isSearching ? "Konum alınıyor" : status === "fixed" && mode === "gps" ? "Konum Alındı" : "Konumumu Bul"}
              </button>

              <button type="button" className="vloc-btn vloc-btn--danger" onClick={clearLocation} disabled={!selected && mode === "none"}>
                <XMarkIcon className="vloc-btn-ic" aria-hidden="true" />
                Temizle
              </button>
            </div>
          </div>

          <div className="vloc-hint" role="note">
            {status === "searching"
              ? "Konum alınıyor…"
              : mode === "gps" && status === "fixed" && typeof accuracy === "number"
              ? `GPS doğruluğu: ±${accuracy} m${typeof avgAccuracy === "number" ? ` (ortalama ±${avgAccuracy} m)` : ""}`
              : mode === "manual"
              ? "Haritadan seçilen konum kullanılacak."
              : required
              ? "Konum seçimi gerekli."
              : "İsteğe bağlı olarak konum seçebilirsiniz."}
          </div>

          {infoText ? (
            <div className="vloc-alert" role="alert">
              <ExclamationTriangleIcon className="vloc-alert-ic" aria-hidden="true" />
              <div className="vloc-alert-text">{infoText}</div>
            </div>
          ) : null}
        </>
      ) : null}

      {/* Map */}
      <div className="vloc-map">
        {/* Compact overlay controls (icon-only) */}
        {compact ? (
          <div className="vloc-map-controls" aria-label="Konum kontrolleri">
            <button
  type="button"
  className={`vloc-icon-btn vloc-icon-btn--with-text ${gpsBtn.state === "fixed" ? "is-ok" : ""}`}
  onClick={locateMe}
  disabled={isSearching}
  aria-label="GPS ile konumumu bul"
  title="GPS ile konumumu bul"
>
  <gpsBtn.Icon className={`vloc-icon-ic ${gpsBtn.spin ? "spin" : ""}`} aria-hidden="true" />
  <span className="vloc-icon-btn-text">
    {isSearching ? "Konum alınıyor" : gpsBtn.state === "fixed" ? "Konum alındı" : "Konum bul"}
  </span>
</button>


            <button
              type="button"
              className="vloc-icon-btn is-danger"
              onClick={clearLocation}
              disabled={!selected && mode === "none"}
              aria-label="Konumu temizle"
              title="Konumu temizle"
            >
              <XMarkIcon className="vloc-icon-ic" aria-hidden="true" />
            </button>
          </div>
        ) : null}

        {/* Compact error banner (minimal) */}
        {compact && infoText ? (
          <div className="vloc-map-banner" role="alert">
            <ExclamationTriangleIcon className="vloc-map-banner-ic" aria-hidden="true" />
            <div className="vloc-map-banner-txt">{infoText}</div>
          </div>
        ) : null}

        <MapContainer
          center={selected || boundaryView?.center || defaultCenter}
          zoom={selected ? autoZoom : boundaryView?.zoom || defaultZoom}
          minZoom={minZoom}
          zoomSnap={0.5}
          zoomDelta={0.5}
          style={{ height: "100%", width: "100%" }} // height is controlled by CSS (.vloc-map)
          whenCreated={handleMapCreated}
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

          {/* Manual click always enabled; clicking cancels GPS watch and switches to manual */}
          <MapClick disabled={false} onPick={handlePick} />

          {selected ? <Marker position={selected} /> : null}

          {/* Accuracy zone (GPS mode only) */}
          {mode === "gps" && selected && zone ? (
            <Polygon positions={zone} pathOptions={{ className: "vloc-zone" }} />
          ) : null}
        </MapContainer>
      </div>
    </div>
  );
}
